import { X } from 'lucide-react'
import { useEffect, useEffectEvent, useId, useRef, type ReactNode, type RefObject } from 'react'
import { IconButton } from './Button'
import { cx } from './cx'

/**
 * Drives a native modal <dialog> from a controlled `open` prop: showModal() gives the focus trap, inert
 * background, top layer and Esc for free. Focus returns to the element that opened it.
 */
export function useModal(open: boolean, onClose: () => void): RefObject<HTMLDialogElement | null> {
  const ref = useRef<HTMLDialogElement>(null)
  const close = useEffectEvent(onClose)

  useEffect(() => {
    const dialog = ref.current
    if (!open || !dialog) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    // Esc fires `cancel`: stay controlled — ask the owner instead of closing natively.
    const onCancel = (e: Event) => {
      e.preventDefault()
      close()
    }
    // A press that starts and ends on the <dialog> element itself is on the backdrop (content fills the box);
    // checking both ends ignores text selections dragged out of the dialog.
    let downOnBackdrop = false
    const onPointerDown = (e: PointerEvent) => {
      downOnBackdrop = e.target === dialog
    }
    const onClick = (e: MouseEvent) => {
      if (downOnBackdrop && e.target === dialog) close()
    }
    dialog.addEventListener('cancel', onCancel)
    dialog.addEventListener('pointerdown', onPointerDown)
    dialog.addEventListener('click', onClick)
    dialog.showModal()
    // React's autoFocus runs before showModal() and is lost; mark the intended first focus with data-autofocus.
    dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus()
    return () => {
      dialog.removeEventListener('cancel', onCancel)
      dialog.removeEventListener('pointerdown', onPointerDown)
      dialog.removeEventListener('click', onClick)
      if (dialog.open) dialog.close()
      // Browsers restore focus on close(), but not when React has already removed the dialog.
      if (opener?.isConnected) opener.focus({ preventScroll: true })
    }
  }, [open])

  return ref
}

type DialogProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  /** Says what happens, in context ("Das Training am Di, 14.10. wird für 22 Spieler abgesagt."). */
  description?: ReactNode
  children?: ReactNode
  /** Buttons, right-aligned; the primary action last. */
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** role="alertdialog" for confirmations that interrupt. */
  alert?: boolean
}

const widths = { sm: 'md:max-w-[420px]', md: 'md:max-w-[520px]', lg: 'md:max-w-[720px]' }

const surface =
  'm-0 max-h-none max-w-none border-0 bg-surface p-0 text-ink shadow-float outline-none backdrop:animate-fade'

export function Dialog({ open, onClose, title, description, children, footer, size = 'md', alert }: DialogProps) {
  const ref = useModal(open, onClose)
  const titleId = useId()
  const descId = useId()
  if (!open) return null
  return (
    <dialog
      ref={ref}
      role={alert ? 'alertdialog' : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      className={cx(
        surface,
        // Phone: bottom sheet, full width, thumb-reachable actions. md+: centred card.
        'fixed inset-x-0 top-auto bottom-0 w-full animate-rise rounded-t-card',
        'md:inset-0 md:m-auto md:h-fit md:w-[calc(100%-2rem)] md:animate-pop md:rounded-card',
        widths[size],
      )}
    >
      <div className="flex max-h-[min(88dvh,800px)] flex-col">
        <div className="flex items-start justify-between gap-4 px-5 pt-5 md:px-6 md:pt-6">
          <div className="flex min-w-0 flex-col gap-1.5">
            <h2 id={titleId} className="text-heading text-ink">
              {title}
            </h2>
            {description && (
              <p id={descId} className="text-body text-ink-2">
                {description}
              </p>
            )}
          </div>
          <IconButton label="Schließen" icon={X} size="sm" tooltip={false} onClick={onClose} className="-mt-1 -mr-2" />
        </div>
        {children && <div className="min-h-0 overflow-y-auto px-5 pt-5 md:px-6">{children}</div>}
        {/* Without a footer the content ends here: keep it clear of the home indicator. */}
        <div className={cx('shrink-0 md:h-6', footer ? 'h-5' : 'h-[max(1.25rem,env(safe-area-inset-bottom))]')} />
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-line px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end md:px-6">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  )
}

type SheetProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  /** Header-right actions next to close (e.g. "Alle gelesen"). */
  actions?: ReactNode
  children: ReactNode
  footer?: ReactNode
}

/** Drawer: from the right on desktop, from the bottom on phones. Notifications, quick edit. */
export function Sheet({ open, onClose, title, description, actions, children, footer }: SheetProps) {
  const ref = useModal(open, onClose)
  const titleId = useId()
  if (!open) return null
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={cx(
        surface,
        'fixed inset-x-0 top-auto bottom-0 h-[min(88dvh,720px)] w-full animate-rise rounded-t-card',
        'md:inset-y-0 md:right-0 md:left-auto md:h-dvh md:w-[440px] md:animate-slide-left md:rounded-none md:border-l md:border-line',
      )}
    >
      <div className="flex h-full flex-col">
        {/* Grab handle: a visual cue only, the sheet closes via button, Esc or backdrop. */}
        <div aria-hidden="true" className="mx-auto mt-2 h-1 w-9 rounded-full bg-line-strong md:hidden" />
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4 md:px-6 md:py-5">
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 id={titleId} className="text-heading text-ink">
              {title}
            </h2>
            {description && <p className="text-small text-ink-2">{description}</p>}
          </div>
          <div className="-mr-2 flex shrink-0 items-center gap-1">
            {actions}
            <IconButton label="Schließen" icon={X} size="sm" tooltip={false} onClick={onClose} />
          </div>
        </div>
        <div className={cx('min-h-0 flex-1 overflow-y-auto px-5 pt-4 md:px-6', footer ? 'pb-4' : 'pb-[max(1rem,env(safe-area-inset-bottom))]')}>{children}</div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-line px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end md:px-6">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  )
}
